import React, { useState } from 'react';
import { customersAPI } from '../services/api';
import '../styles/CustomerCreationModal.css';

interface CustomerCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomerCreated: (customer: any) => void;
}

const CUSTOMER_TYPES = [
  { value: 'LOCAL_WILL_CALL', label: 'Local Will Call' },
  { value: 'WHOLESALE_FARM', label: 'Wholesale Farm' },
  { value: 'RETAIL_PARTNER', label: 'Retail Partner' },
  { value: 'OTHER', label: 'Other' },
  { value: 'CJ_CUSTOMER', label: 'CJ Customer' },
];

const PAYMENT_TERMS = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_10', label: 'Net 10' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_45', label: 'Net 45' },
  { value: 'net_60', label: 'Net 60' },
];

const TERMS_START = [
  { value: 'on_order', label: 'On Order Date' },
  { value: 'on_ship', label: 'On Ship Date' },
  { value: 'on_delivery', label: 'On Delivery Date' },
];

const CustomerCreationModal: React.FC<CustomerCreationModalProps> = ({
  isOpen,
  onClose,
  onCustomerCreated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    customer_type: 'LOCAL_WILL_CALL',
    phone: '',
    email: '',
    contact_person: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    payment_terms: 'due_on_receipt',
    payment_terms_start: 'on_ship',
    credit_limit: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await customersAPI.create({
        ...formData,
        is_active: true,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
        current_balance: 0,
        outstanding_balance: 0,
      });

      onCustomerCreated(response.data);
      setFormData({
        name: '',
        customer_type: 'LOCAL_WILL_CALL',
        phone: '',
        email: '',
        contact_person: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        payment_terms: 'due_on_receipt',
        payment_terms_start: 'on_ship',
        credit_limit: '',
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content customer-modal">
        <div className="modal-header">
          <h2>Create New Customer</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="form-row">
              <div className="form-group required">
                <label htmlFor="name">Customer Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter customer name"
                />
              </div>

              <div className="form-group required">
                <label htmlFor="customer_type">Customer Type</label>
                <select
                  id="customer_type"
                  name="customer_type"
                  value={formData.customer_type}
                  onChange={handleChange}
                  required
                >
                  {CUSTOMER_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group required">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="555-1234"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="customer@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="contact_person">Contact Person</label>
              <input
                type="text"
                id="contact_person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                placeholder="John Doe"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Springfield"
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="IL"
                  maxLength={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="postal_code">Postal Code</label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="12345"
                />
              </div>
            </div>

            <div className="form-section-header">Payment Terms</div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payment_terms">Payment Terms</label>
                <select
                  id="payment_terms"
                  name="payment_terms"
                  value={formData.payment_terms}
                  onChange={handleChange}
                >
                  {PAYMENT_TERMS.map((term) => (
                    <option key={term.value} value={term.value}>
                      {term.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="payment_terms_start">Terms Start</label>
                <select
                  id="payment_terms_start"
                  name="payment_terms_start"
                  value={formData.payment_terms_start}
                  onChange={handleChange}
                >
                  {TERMS_START.map((start) => (
                    <option key={start.value} value={start.value}>
                      {start.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="credit_limit">Credit Limit (optional)</label>
              <input
                type="text"
                inputMode="decimal"
                id="credit_limit"
                name="credit_limit"
                value={formData.credit_limit}
                onChange={handleChange}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0) {
                    setFormData((prev) => ({
                      ...prev,
                      credit_limit: val.toFixed(2),
                    }));
                  }
                }}
                placeholder="Leave blank for no limit"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerCreationModal;
